import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { clean, stagger } from '../utils/format';
import { useIsEditing, useMediaQuery } from '../utils/hooks';
import { PATHS, pagePath } from '../utils/site';
import { DetailsRow } from '../utils/details';
import type { Amenity } from './AmenitiesList';

const amenitiesLinkDef: SlotGroupDefinition = {
  label: 'All Amenities Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'See All Amenities' },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** Columns follow the count, as on the amenities page: one below 8, two to 24, three above. */
const cols = (n: number) => (n < 8 ? 1 : n <= 24 ? 2 : 3);

const alist = (c: number) =>
  `mt-[34px] grid grow content-start gap-y-0 border-t border-t-[--q-secondary] [&_div]:border-b [&_div]:border-b-[--q-border] [&_div]:px-0 [&_div]:font-heading max-[1199px]:mt-0 max-[767px]:mt-3 max-[767px]:grid-cols-[1fr] max-[767px]:gap-0 ${
    c === 3
      ? 'grid-cols-[repeat(3,1fr)] gap-x-8 [&_div]:py-3 [&_div]:text-[19px] [&_div]:leading-[25px]'
      : `${c === 1 ? 'grid-cols-[1fr]' : 'grid-cols-[1fr_1fr]'} gap-x-10 [&_div]:py-3.5 [&_div]:text-[22px] [&_div]:leading-7 max-[767px]:[&_div]:py-3 max-[767px]:[&_div]:text-[19px] max-[767px]:[&_div]:leading-[25px]`
  }`;

/** The single-column list as it sits inside a phone details row. */
const detailsList =
  'grid grow grid-cols-[1fr] content-start gap-x-10 gap-y-0 [border-top:0] [&_div]:border-b [&_div]:border-b-[--q-border] [&_div]:px-0 [&_div]:py-2.5 [&_div]:font-heading [&_div]:text-[17px] [&_div]:leading-6 max-[767px]:gap-0';

/** "Washer and dryer, hardwood floors, high ceilings + 8 more" */
function summary(titles: string[]): string {
  const words = titles.slice(0, 3).map((t, i) => {
    const s = t.replace(/\s*\/\s*/g, ' and ');
    return i === 0 ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s.toLowerCase();
  });
  return `${words.join(', ')}${titles.length > 3 ? ` + ${titles.length - 3} more` : ''}`;
}

/**
 * AmenitiesList · Your home on the floor plans page (Paper 04 notes). On phones it opens the "Before you apply"
 * list: its heading, then this section's row; the fee, pet and tour sections below add theirs.
 */
export function HomeFeatures() {
  const editing = useIsEditing();
  const phone = useMediaQuery('(max-width: 767px)');
  const home = useCollectionBinding<Amenity>('homefeatures.items', { collection: 'amenities', filter: { group: 'Apartment' } });
  const link = useSlotGroup('homefeatures.amenities_link', amenitiesLinkDef);
  const titles = home.map((a) => clean(a.title)).filter((t): t is string => !!t);
  const showLink = !!pagePath('amenities');

  const allLink = (
    <EditableSlotGroup groupPath="homefeatures.amenities_link" definition={amenitiesLinkDef}>
      {showLink && (
        <a className="textlink" href={link.url || PATHS.amenities} target={link._target || '_self'}>
          {link.label || 'See All Amenities'} <span className="btn__arrow">→</span>
        </a>
      )}
    </EditableSlotGroup>
  );
  const list = (
    <EditableCollectionBinding bindingPath="homefeatures.items" label="Your Home Amenities" className={phone ? detailsList : alist(cols(titles.length))} style={stagger(1)}>
      {titles.map((t, i) => (
        <div key={i}>{t}</div>
      ))}
    </EditableCollectionBinding>
  );

  if (phone) {
    return (
      <section className="bg-[--q-paper] px-[--gutter] pt-8">
        <div className="flex flex-col gap-1 pb-3 pt-6">
          <RichTextContent path="homefeatures.details_eyebrow" as="div" className="eyebrow [&.eyebrow]:text-[11px] [&.eyebrow]:leading-[14px]" />
          <RichTextContent path="homefeatures.details_title" as="h2" className="m-0 font-heading text-[28px] font-light leading-[34px] [&_p]:m-0" />
        </div>
        {(titles.length > 0 || editing) && (
          <DetailsRow id="your-home" first title={<RichTextContent path="homefeatures.row_title" as="span" />} summary={summary(titles)}>
            {list}
            <div className="[&_.textlink]:inline-flex [&_.textlink]:min-h-11 [&_.textlink]:items-center">{allLink}</div>
          </DetailsRow>
        )}
      </section>
    );
  }

  if (!titles.length && !editing) return null;

  return (
    <section className="section homefeatures" id="your-home">
      <div className="flex gap-16 max-[1199px]:flex-col max-[1199px]:gap-6">
        <div className="flex w-[400px] shrink-0 flex-col gap-4 max-[1199px]:w-auto" data-reveal>
          <RichTextContent path="homefeatures.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="homefeatures.title" as="h2" className="h3 [&_p]:m-0" />
          <RichTextContent path="homefeatures.lead" as="p" className="m-0 text-[15px] leading-6 text-muted-foreground" />
          <div className="pt-1">{allLink}</div>
        </div>
        {list}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeFeatures',
  displayName: 'Your Home Features',
  tokens: {
    'homefeatures.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Your home</p>' },
    'homefeatures.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Inside every <span data-type="field-token" data-field="property_name"></span> home</p>',
    },
    'homefeatures.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>What comes with the home before you add a thing.</p>' },
    'homefeatures.details_eyebrow': { label: 'Details List Eyebrow (phone)', type: 'richtext', defaultValue: '<p>Before you apply</p>' },
    'homefeatures.details_title': { label: 'Details List Title (phone)', type: 'richtext', defaultValue: '<p>The details, in one place.</p>' },
    'homefeatures.row_title': { label: 'Row Title (phone)', type: 'richtext', defaultValue: '<p>In every home</p>' },
  },
  slotGroups: {
    'homefeatures.amenities_link': amenitiesLinkDef,
  },
  collectionBindings: {
    'homefeatures.items': {
      label: 'Your Home Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
  },
};

(HomeFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeFeatures;
